import { useState, useMemo } from 'react'
import type { PrescriptionDetail } from '../../store/prescriptionsSlice'

interface DosageCalculatorProps {
  prescription: PrescriptionDetail
  onApply: (data: { dosage_calculated: number; dose_rate: number }) => Promise<void>
}

export function DosageCalculator({ prescription, onApply }: DosageCalculatorProps) {
  const defaultWeight = prescription.patient_weight ? Number(prescription.patient_weight) : 0
  const defaultUnit = prescription.patient_weight_unit || 'kg'
  const defaultDoseRate = prescription.dose_rate ?? prescription.medication_dose_rate ?? 0

  const [weight, setWeight] = useState(String(defaultWeight))
  const [weightUnit, setWeightUnit] = useState(defaultUnit)
  const [doseRate, setDoseRate] = useState(String(defaultDoseRate))

  const calculatedDosage = useMemo(() => {
    const w = parseFloat(weight) || 0
    const dr = parseFloat(doseRate) || 0
    let weightKg = w
    if (weightUnit === 'lbs') {
      weightKg = w / 2.205
    }
    return dr * weightKg
  }, [weight, weightUnit, doseRate])

  const handleApply = async () => {
    await onApply({
      dosage_calculated: Math.round(calculatedDosage * 100) / 100,
      dose_rate: parseFloat(doseRate) || 0,
    })
  }

  return (
    <div data-testid="dosage-calculator-section" className="border border-border rounded-[6px] bg-surface">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="text-[14px] font-medium text-text-primary">Dosage Calculator</h2>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-3 gap-4 max-sm:grid-cols-1">
          <div>
            <label className="text-[12px] text-text-muted block mb-1">Patient Weight</label>
            <div className="flex items-center gap-2">
              <input
                data-testid="calculator-weight-input"
                type="number"
                step="0.01"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="h-7 w-24 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
              />
              <div className="flex items-center h-7 border border-border rounded-[4px] overflow-hidden">
                <button
                  data-testid="calculator-unit-kg"
                  onClick={() => setWeightUnit('kg')}
                  className={`h-full px-2 text-[12px] cursor-pointer transition-colors duration-100 ${
                    weightUnit === 'kg'
                      ? 'bg-accent text-white'
                      : 'bg-base text-text-muted hover:bg-hover'
                  }`}
                >
                  kg
                </button>
                <button
                  data-testid="calculator-unit-lbs"
                  onClick={() => setWeightUnit('lbs')}
                  className={`h-full px-2 text-[12px] cursor-pointer transition-colors duration-100 ${
                    weightUnit === 'lbs'
                      ? 'bg-accent text-white'
                      : 'bg-base text-text-muted hover:bg-hover'
                  }`}
                >
                  lbs
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="text-[12px] text-text-muted block mb-1">Dose Rate (mg/kg)</label>
            <input
              data-testid="calculator-dose-rate-input"
              type="number"
              step="0.01"
              value={doseRate}
              onChange={(e) => setDoseRate(e.target.value)}
              className="h-7 w-full px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
            />
          </div>

          <div>
            <label className="text-[12px] text-text-muted block mb-1">Calculated Dosage</label>
            <div
              data-testid="calculator-result"
              className="h-7 flex items-center px-2 text-[13px] font-medium text-text-primary border border-border rounded-[4px] bg-accent-subtle-bg"
            >
              {calculatedDosage.toFixed(2)} mg
            </div>
          </div>
        </div>

        <div className="mt-4">
          <button
            data-testid="apply-to-prescription-button"
            onClick={handleApply}
            className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
          >
            Apply to Prescription
          </button>
        </div>
      </div>
    </div>
  )
}
